<template>
	<measurements-height-transition appear mode="out-in">
		<div class="measurements-selector__pricing" v-if="shouldShowTable && bulkPrices.length > 0">
			<table class="measurements-selector__pricing-table">
				<caption>
					{{ i18n.meta.bulkDiscount }}
				</caption>
				<thead>
					<tr>
						<td class="unit-quantity"><strong>{{ i18n.quantity }}</strong></td>
						<td class="unit-price"><strong>{{ i18n.unitPrice }}</strong></td>
						<td class="unit-quantity"><strong>{{ i18n.quantity }}</strong></td>
						<td class="unit-price"><strong>{{ i18n.unitPrice }}</strong></td>
					</tr>
				</thead>
				<tbody class="kps_bulk_price_table_body">
					<tr v-for="(row, i) in leftColumn" :key="row.quantity">
						<td>{{ leftColumn[ i ].quantity }}</td>
						<td><strong><FormattedPrice :price="leftColumn[ i ].price" /></strong></td>
						<td>{{ ( rightColumn[ i ] || {} ).quantity }}</td>
						<td><strong><FormattedPrice :price="( rightColumn[ i ] || {} ).price" /></strong></td>
					</tr>
				</tbody>
			</table>
		</div>
	</measurements-height-transition>
</template>

<script>
import mapValues from "lodash/mapValues";
import memoize from "lodash/memoize";
import Length from "../../../source/js/shared/models/Geometry/Length";
import { getMeasurementsCacheKey } from "@/functions/cache";
import { mapGetters, mapMutations } from "vuex";
import FormattedPrice from "@/components/FormattedPrice";
import MeasurementsHeightTransition from '@/components/UI/MeasurementsHeightTransition.vue';

export default {
	components: { FormattedPrice, MeasurementsHeightTransition },
	props: {
		price: Number,
		shape: String,
		primitives: Object,
		productId: String,
	},
	emits: ['discounted-price', 'quantity'],
	data: function() {
		return {
			i18n: this.$i18n,
			kpsSettings: this.$kpsSettings,
			requestCount: 0,
			bulkPrices: [],
			lastRequest: '',
			quantity: 1,
		};
	},
	created() {
		this.retrieveBulkItems = memoize( this.retrieveBulkItems, this.getCacheKey );
	},
	mounted() {
		jQuery( () => {
			const $quantityInput = jQuery( 'div.quantity input.input-text.qty' );
			if($quantityInput.length === 0) {
				return;
			}
			const self = this; //eslint-disable-line @typescript-eslint/no-this-alias

			self.quantity = parseInt( $quantityInput.val(), 10 );
			$quantityInput.on( 'change', () => {
				self.quantity = parseInt( $quantityInput.val(), 10 );
			} );
		} );
	},
	computed: {
		...mapGetters( [
			'activeEdgeFinish',
		] ),
		shouldShowTable() {
			const minimumPrice = parseFloat( this.kpsSettings.settings.minimumPrice );

			return 0 !== minimumPrice && this.price === minimumPrice;
		},
		hasTwoColumns() {
			return this.bulkPrices.length > 5;
		},
		halfPoint() {
			return Math.ceil( this.bulkPrices.length / 2 );
		},
		leftColumn() {
			return this.hasTwoColumns ? this.bulkPrices.slice( 0, this.halfPoint ): this.bulkPrices;
		},
		rightColumn() {
			return this.hasTwoColumns ? this.bulkPrices.slice( this.halfPoint ) : [];
		},
		rows() {

			// Create 2 columns when there are more than 5 items
			if ( this.bulkPrices.length <= 5 ) {
				return this.bulkPrices;
			}
			return [];
		},
		discountedPrice() {
			if ( ! this.shouldShowTable ) {
				return this.price;
			}

			// If the quantity doesn't meet the first discount, bail.
			if ( this.bulkPrices.length === 0 || this.quantity < this.bulkPrices[ 0 ] ) {
				return this.price;
			}

			// Try to match the last one
			if ( this.quantity >= this.bulkPrices[ this.bulkPrices.length - 1 ].quantity ) {
				return this.bulkPrices[ this.bulkPrices.length - 1 ].price;
			}

			// Match everything except the last one
			for ( let i = 0; i < this.bulkPrices.length - 1; i++ ) {
				if (
					this.quantity >= this.bulkPrices[ i ].quantity &&
					this.quantity < this.bulkPrices[ i + 1 ].quantity
				) {
					return this.bulkPrices[ i ].price;
				}
			}

			return this.price;
		}
	},
	methods: {
		async refreshTable( shape, primitives ) {

			// Immediately emit the discounted price, because we know there is no bulk discount.
			if ( ! this.shouldShowTable ) {
				this.$emit( 'discounted-price', this.discountedPrice );
				this.$emit( 'quantity', this.quantity );
				return;
			}

			const thisRequest = this.getCacheKey( shape, primitives, this.activeEdgeFinish );
			this.lastRequest = thisRequest;

			const response = await this.retrieveBulkItems( shape, primitives, this.activeEdgeFinish );

			// When multiple AJAX requests are dispatched, we need to make sure we only
			// update the prices with the last one.
			if ( this.lastRequest === thisRequest ) {
				this.bulkPrices = response.prices || [];
				this.$emit( 'discounted-price', this.discountedPrice );
				this.$emit( 'quantity', this.quantity );
			}
		},
		async retrieveBulkItems( shape, primitives, edgeFinish ) {
			this.requestCount++;

			primitives = mapValues( primitives, ( dimensionValue, dimensionName ) => {
				if ( 'number_of_points' === dimensionName ) {
					return dimensionValue;
				}

				return Length.from_cm( dimensionValue ).in_mm();
			} );

			const params = Object.keys( primitives ).map( ( dimension ) => {
				return `measurements[${ dimension }]=${ primitives[ dimension ] }`;
			} );
			params.push( `measurements[shape]=${ shape }` );
			params.push( `sawing_method=${edgeFinish}` );
			params.push( `product=${ this.kpsSettings.product_id }` );
			params.push( `request_id=${ this.requestCount }` );

			const requestUrl = `${ this.kpsSettings.restUrl }kps/v1/bulkprice?` + params.join( '&' );

			this.updateIsRetrievingBulkPricing( true );

			// This turns the jQuery deferred into an ES6 Promise.
			const results = await Promise.resolve( jQuery.get( requestUrl ) );

			this.updateIsRetrievingBulkPricing( false );

			return results;
		},
		getCacheKey( shape, primitives) {
			return getMeasurementsCacheKey( { shape, primitives, edgeFinish: this.activeEdgeFinish } );
		},
		...mapMutations( [
			'updateIsRetrievingBulkPricing',
		] )
	},
	watch: {
		activeEdgeFinish( oldShape, newShape ) {
			if ( oldShape !== newShape ) {
				this.refreshTable( this.shape, this.primitives );
			}
		},
		shape( oldShape, newShape ) {
			if ( oldShape !== newShape ) {
				this.refreshTable( this.shape, this.primitives );
			}
		},
		primitives( oldDimensions, newDimensions ) {
			if ( oldDimensions !== newDimensions ) {
				this.refreshTable( this.shape, this.primitives );
			}
		},
		price( oldPrice, newPrice ) {
			if ( oldPrice !== newPrice ) {
				this.refreshTable( this.shape, this.primitives );
			}
		},
		quantity( newQuantity, oldQuantity ) {
			if ( newQuantity !== oldQuantity ) {
				this.refreshTable( this.shape, this.primitives );
			}
		}
	}
};
</script>
